import { supabase } from "@/integrations/supabase/client";

export interface WelcomeFormData {
  firstName: string;
  lastName: string;
  organizationName: string;
}

export interface WelcomeFormResult {
  success: boolean;
  errors: string[];
  data?: {
    profileUpdated: boolean;
    organizationUpdated: boolean;
  };
}

/**
 * Atomically updates user profile and organization name for welcome form submission
 * Uses Promise.allSettled to ensure both operations are attempted concurrently
 * Provides detailed error reporting and success status
 */
export const welcomeFormService = {
  /**
   * Save welcome form data with atomic operations
   */
  async saveWelcomeFormData(
    userId: string,
    organizationId: string,
    data: WelcomeFormData
  ): Promise<WelcomeFormResult> {
    console.log("Starting atomic welcome form save:", { userId, organizationId, data });

    try {
      // Execute both operations concurrently
      const [profileResult, orgResult] = await Promise.allSettled([
        // Update user profile
        supabase
          .from("profiles")
          .upsert({
            id: userId,
            first_name: data.firstName.trim(),
            last_name: data.lastName.trim(),
          }, { onConflict: 'id' }),
        
        // Update organization name
        supabase
          .from("organizations")
          .update({
            name: data.organizationName.trim(),
            agent_url: data.organizationName.trim()
          })
          .eq("id", organizationId)
      ]);

      // Collect errors from failed operations
      const errors: string[] = [];
      let profileUpdated = false;
      let organizationUpdated = false;

      // Check profile update result
      if (profileResult.status === 'rejected') {
        errors.push(`Profile update failed: ${profileResult.reason}`);
      } else if (profileResult.value?.error) {
        errors.push(`Profile update failed: ${profileResult.value.error.message}`);
      } else {
        profileUpdated = true;
        console.log("Profile updated successfully");
      }

      // Check organization update result
      if (orgResult.status === 'rejected') {
        errors.push(`Organization update failed: ${orgResult.reason}`);
      } else if (orgResult.value?.error) {
        errors.push(`Organization update failed: ${orgResult.value.error.message}`);
      } else {
        organizationUpdated = true;
        console.log("Organization updated successfully");
      }

      // Determine overall success
      const success = errors.length === 0;

      if (success) {
        console.log("Welcome form data saved successfully");
      } else {
        console.error("Welcome form save completed with errors:", errors);
      }

      return {
        success,
        errors,
        data: {
          profileUpdated,
          organizationUpdated,
        },
      };

    } catch (error) {
      console.error("Unexpected error in welcome form save:", error);
      return {
        success: false,
        errors: [`Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`],
      };
    }
  },

  /**
   * Load existing welcome form data for returning users
   */
  async loadWelcomeFormData(
    userId: string,
    organizationId: string
  ): Promise<{
    success: boolean;
    data?: WelcomeFormData;
    error?: string;
  }> {
    try {
      console.log("Loading welcome form data:", { userId, organizationId });

      // Load user profile and organization data concurrently
      const [profileResult, orgResult] = await Promise.allSettled([
        supabase
          .from("profiles")
          .select("first_name, last_name")
          .eq("id", userId)
          .single(),
        
        supabase
          .from("organizations")
          .select("name")
          .eq("id", organizationId)
          .single()
      ]);

      // Extract data with fallbacks
      const firstName = profileResult.status === 'fulfilled' && !profileResult.value.error
        ? profileResult.value.data?.first_name || ""
        : "";

      const lastName = profileResult.status === 'fulfilled' && !profileResult.value.error
        ? profileResult.value.data?.last_name || ""
        : "";

      const organizationName = orgResult.status === 'fulfilled' && !orgResult.value.error
        ? orgResult.value.data?.name || ""
        : "";

      console.log("Loaded welcome form data:", { firstName, lastName, organizationName });

      return {
        success: true,
        data: {
          firstName,
          lastName,
          organizationName,
        },
      };

    } catch (error) {
      console.error("Error loading welcome form data:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Validate welcome form data
   */
  validateWelcomeFormData(data: WelcomeFormData): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!data.firstName.trim()) {
      errors.push("First name is required");
    }

    if (!data.lastName.trim()) {
      errors.push("Last name is required");
    }

    if (!data.organizationName.trim()) {
      errors.push("Organization name is required");
    }

    // Additional validation rules
    if (data.firstName.trim().length > 100) {
      errors.push("First name must be less than 100 characters");
    }

    if (data.lastName.trim().length > 100) {
      errors.push("Last name must be less than 100 characters");
    }

    if (data.organizationName.trim().length > 200) {
      errors.push("Organization name must be less than 200 characters");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
}; 